/**
 * The exposure meter: what makes the live 3D view and the Blender render agree
 * on how bright a room is.
 *
 * The two renderers light a room by different rules (the view normalises a
 * lamp to its nearest wall, Blender gives it watts by floor area; the view's
 * environment light falls everywhere, Blender's HDRI comes in through the
 * glass), so the same look came out 1.3 stops apart in one room and level in
 * another (measured 2026-10-29). No fixed multiplier can close a gap that
 * changes with the room. So each side meters its own picture in scene-linear
 * light — before any tone curve — and sets its exposure so that the 70th
 * percentile of luminance (the lit boards of a mostly-white room, above the
 * floor and the shadows, below the window) lands on METER_TARGET. A look's
 * Exposure is then an offset from that, and its HDRI and lamp strengths set
 * the balance of the light, never the level.
 *
 * scripts/blender/render_room.py has the same constants and the same
 * percentile (meter_correction), plus a fixed 0.85 stops under AgX, which shows
 * the metered level that much darker than this view's tone curve does;
 * test_render_settings.py reads the constants from here.
 */

/** Scene-linear luminance the 70th percentile is brought to: a white board well lit, not blown. */
export const METER_TARGET = 0.5;
/** Which percentile of the picture's luminance is metered (0 … 1). */
export const METER_PERCENTILE = 0.7;
/** The most the meter may push the exposure either way, in stops. */
export const METER_CLAMP_EV = 2.5;
/** The fewest pixels a reading needs; fewer (an empty view) and the meter says nothing. */
export const METER_MIN_PIXELS = 50;

/**
 * Stops to add to the exposure so the metered percentile of these scene-linear
 * luminances lands on the target; null when there is nothing to meter.
 */
export function meterCorrection(lums: ArrayLike<number>): number | null {
  const n = lums.length;
  if (n < METER_MIN_PIXELS) return null;
  const sorted = Array.from(lums as ArrayLike<number>).filter((v) => Number.isFinite(v) && v >= 0).sort((a, b) => a - b);
  if (sorted.length < METER_MIN_PIXELS) return null;
  const p = sorted[Math.min(sorted.length - 1, Math.floor(METER_PERCENTILE * (sorted.length - 1)))];
  if (p <= 1e-5) return METER_CLAMP_EV;
  return Math.max(-METER_CLAMP_EV, Math.min(METER_CLAMP_EV, Math.log2(METER_TARGET / p)));
}
